import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Container,
  useMediaQuery,
} from "@mui/material";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { styled, alpha } from "@mui/material/styles";
import Header from "../../components/Header";
import BarChart from "../../components/BarChart";
import StatBox from "../../components/StatBox";
import AlerteService from "../../api/services/AlerteService";
import SocieteService from "../../api/services/SocieteService";
import AlerteDetailsModal from "../../components/AlerteDetailsModal";

const StyledCard = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha("#2a5fa9", 0.05)} 0%, ${alpha("#65b986", 0.05)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 20px 40px ${alpha("#2a5fa9", 0.1)}`,
    border: `1px solid ${alpha("#2a5fa9", 0.2)}`,
  },
  padding: { xs: "12px", sm: "20px", md: "24px" },
  [theme.breakpoints.down("sm")]: { borderRadius: "12px" },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, #2a5fa9 0%, #65b986 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 700,
  fontSize: "clamp(1rem, 4vw, 1.5rem)",
  marginBottom: "12px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
  },
}));

const Dashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleItemUpdated = async () => {
    try {
      const alertesData = await AlerteService.getAlertes(token, role);
      if (!Array.isArray(alertesData)) return;
      setAlertes(alertesData);

      const compteurs = { Marque: {}, Brevet: {}, Domaine: {}, Contrat: {} };
      alertesData.forEach((alerte) => {
        const nomAlerte = (alerte.nom_alerte || "Inconnu").trim();
        const filtreSociete = (item) => !societeFilter || item.societe_id == societeFilter;

        (alerte.marques || []).filter(filtreSociete).forEach(() => {
          compteurs.Marque[nomAlerte] = (compteurs.Marque[nomAlerte] || 0) + 1;
        });
        (alerte.brevets || []).filter(filtreSociete).forEach(() => {
          compteurs.Brevet[nomAlerte] = (compteurs.Brevet[nomAlerte] || 0) + 1;
        });
        (alerte.contrats || []).filter(filtreSociete).forEach(() => {
          compteurs.Contrat[nomAlerte] = (compteurs.Contrat[nomAlerte] || 0) + 1;
        });
        (alerte.nomsDomaine || []).filter(filtreSociete).forEach(() => {
          compteurs.Domaine[nomAlerte] = (compteurs.Domaine[nomAlerte] || 0) + 1;
        });
      });

      setAlerteMarques(Object.values(compteurs.Marque).reduce((a, b) => a + b, 0));
      setAlerteBrevets(Object.values(compteurs.Brevet).reduce((a, b) => a + b, 0));
      setAlerteDomaines(Object.values(compteurs.Domaine).reduce((a, b) => a + b, 0));
      setAlerteContrats(Object.values(compteurs.Contrat).reduce((a, b) => a + b, 0));

      const formatChart = (obj) =>
        Object.entries(obj).map(([x, y]) => ({ x, y })).sort((a, b) => b.y - a.y).slice(0, isMobile ? 5 : 10);

      setChartMarque(formatChart(compteurs.Marque));
      setChartBrevet(formatChart(compteurs.Brevet));
      setChartDomaine(formatChart(compteurs.Domaine));
      setChartContrat(formatChart(compteurs.Contrat));

      if (modal.open && modal.alerteData) {
        const alerteUpdated = alertesData.find(a => a.nom_alerte === modal.alerteData.nom_alerte);
        if (alerteUpdated) {
          let items = [];
          switch (modal.type) {
            case "marque": items = alerteUpdated.marques || []; break;
            case "brevet": items = alerteUpdated.brevets || []; break;
            case "domaine": items = alerteUpdated.nomsDomaine || []; break;
            case "contrat": items = alerteUpdated.contrats || []; break;
          }
          if (societeFilter) items = items.filter(i => i.societe_id == societeFilter);
          setModal(prev => ({ ...prev, alerteData: { ...prev.alerteData, items } }));
        }
      }
    } catch (err) {
      console.error("Erreur rechargement alertes:", err);
    }
  };

  const colors = {
    grey: isDark
      ? { 100: "#e0e0e0", 200: "#c2c2c2" }
      : { 100: "#333333", 200: "#4f4f4f" },
  };

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [societes, setSocietes] = useState([]);
  const [societeFilter, setSocieteFilter] = useState("");
  const [loadingSocietes, setLoadingSocietes] = useState(false);

  const [alerteMarques, setAlerteMarques] = useState(0);
  const [alerteBrevets, setAlerteBrevets] = useState(0);
  const [alerteDomaines, setAlerteDomaines] = useState(0);
  const [alerteContrats, setAlerteContrats] = useState(0);

  const role = localStorage.getItem("user_role") || "admin";
  const token = localStorage.getItem("token");

  const [chartMarque, setChartMarque] = useState([]);
  const [chartBrevet, setChartBrevet] = useState([]);
  const [chartDomaine, setChartDomaine] = useState([]);
  const [chartContrat, setChartContrat] = useState([]);

  const [alertes, setAlertes] = useState([]);

  const [modal, setModal] = useState({
    open: false,
    alerteData: null,
    type: null,
  });

  // Chargement des sociétés
  useEffect(() => {
    const loadSocietes = async () => {
      if (!token) return;
      setLoadingSocietes(true);
      try {
        const data = await SocieteService.getAll(token, role);
        setSocietes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Erreur chargement sociétés:", err);
      } finally {
        setLoadingSocietes(false);
      }
    };
    loadSocietes();
  }, [token]); 


  const handleChartClick = (alerteName, type) => {
    const alerte = alertes.find((a) => a.nom_alerte === alerteName);
    if (!alerte) {
      console.warn("Alerte non trouvée:", alerteName);
      return;
    }

    let items = [];
    switch (type) {
      case "marque": items = alerte.marques || []; break;
      case "brevet": items = alerte.brevets || []; break;
      case "domaine": items = alerte.nomsDomaine || []; break;
      case "contrat": items = alerte.contrats || []; break;
      default: items = [];
    }

    if (societeFilter) {
      items = items.filter((item) => item.societe_id == societeFilter);
    }

    setModal({
      open: true,
      alerteData: {
        nom_alerte: alerte.nom_alerte,
        items,
      },
      type,
    });
  };

  const handleCloseModal = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!token) {
      setError("Authentification requise");
      setIsLoading(false);
      return;
    }

    const fetchAll = async () => {
      setIsLoading(true);
      setError("");
      try {
        const alertesData = await AlerteService.getAlertes(token, role);
        if (!Array.isArray(alertesData)) throw new Error("Données invalides");

        setAlertes(alertesData);

        const compteurs = { Marque: {}, Brevet: {}, Domaine: {}, Contrat: {} };
        alertesData.forEach((alerte) => {
          const nomAlerte = (alerte.nom_alerte || "Inconnu").trim();
          const filtreSociete = (item) =>
            !societeFilter || item.societe_id == societeFilter;

          (alerte.marques || []).filter(filtreSociete).forEach(() => {
            compteurs.Marque[nomAlerte] = (compteurs.Marque[nomAlerte] || 0) + 1;
          });
          (alerte.brevets || []).filter(filtreSociete).forEach(() => {
            compteurs.Brevet[nomAlerte] = (compteurs.Brevet[nomAlerte] || 0) + 1;
          });
          (alerte.contrats || []).filter(filtreSociete).forEach(() => {
            compteurs.Contrat[nomAlerte] = (compteurs.Contrat[nomAlerte] || 0) + 1;
          });
          (alerte.nomsDomaine || []).filter(filtreSociete).forEach(() => {
            compteurs.Domaine[nomAlerte] = (compteurs.Domaine[nomAlerte] || 0) + 1;
          });
        });

        setAlerteMarques(Object.values(compteurs.Marque).reduce((a, b) => a + b, 0));
        setAlerteBrevets(Object.values(compteurs.Brevet).reduce((a, b) => a + b, 0));
        setAlerteDomaines(Object.values(compteurs.Domaine).reduce((a, b) => a + b, 0));
        setAlerteContrats(Object.values(compteurs.Contrat).reduce((a, b) => a + b, 0));

        const formatChart = (obj) =>
          Object.entries(obj)
            .map(([x, y]) => ({ x, y }))
            .sort((a, b) => b.y - a.y)
            .slice(0, isMobile ? 5 : 10);

        setChartMarque(formatChart(compteurs.Marque));
        setChartBrevet(formatChart(compteurs.Brevet));
        setChartDomaine(formatChart(compteurs.Domaine));
        setChartContrat(formatChart(compteurs.Contrat));
      } catch (err) {
        console.error("Erreur dashboard:", err);
        setError("Impossible de charger les données");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAll();
  }, [token, societeFilter, isMobile, role]); 

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
        px={2}
      >
        <Typography variant={isMobile ? "h6" : "h5"} textAlign="center">
          Chargement du tableau de bord...
        </Typography>
      </Box>
    );
  }

  const charts = [
    { title: "Répartition des alertes par marque", shortTitle: "Alertes Marques", data: chartMarque, type: "marque" },
    { title: "Répartition des alertes par brevet", shortTitle: "Alertes Brevets", data: chartBrevet, type: "brevet" },
    { title: "Répartition des alertes par domaine", shortTitle: "Alertes Domaines", data: chartDomaine, type: "domaine" },
    { title: "Répartition des alertes par contrat", shortTitle: "Alertes Contrats", data: chartContrat, type: "contrat" },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2.5, md: 3 }, px: { xs: 1, sm: 2, md: 3 } }}>

      {/* En-tête + Filtre */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        gap={{ xs: 1.5, sm: 2 }}
        mb={{ xs: 2, sm: 3 }}
      >
        <Header
          title="DASHBOARD"
          subtitle={isMobile ? "Statistiques" : "Statistiques des alertes"}
        />
        <FormControl
          size={isMobile ? "small" : "medium"}
          sx={{ minWidth: { xs: "100%", sm: 220, md: 260 }, maxWidth: { xs: "100%", sm: 300 } }}
        >
          <InputLabel>{isMobile ? "Sociétés" : "Toutes les sociétés"}</InputLabel>
          <Select
            value={societeFilter}
            label={isMobile ? "Sociétés" : "Toutes les sociétés"}
            onChange={(e) => setSocieteFilter(e.target.value)}
            disabled={loadingSocietes}
            sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.95), borderRadius: "8px" }}
          >
            <MenuItem value="">{isMobile ? "Toutes" : "Toutes les sociétés"}</MenuItem>
            {societes.map((soc) => (
              <MenuItem key={soc.id} value={soc.id}>{soc.nom}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>{error}</Alert>}

      <StyledCard id="dashboard-print">
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>

          {/* StatBox */}
          {[
            { value: alerteMarques, label: "Alertes Marques", shortLabel: "Marques" },
            { value: alerteBrevets, label: "Alertes Brevets", shortLabel: "Brevets" },
            { value: alerteDomaines, label: "Alertes Domaines", shortLabel: "Domaines" },
            { value: alerteContrats, label: "Alertes Contrats", shortLabel: "Contrats" },
          ].map((item, i) => (
            <Grid item xs={6} sm={6} md={3} key={i}>
              <Box
                minHeight={{ xs: "100px", sm: "120px", md: "130px" }}
                p={{ xs: 1.5, sm: 2 }}
                sx={{
                  background: alpha("#4cbed8", 0.05),
                  border: `1px solid ${alpha("#4cbed8", 0.2)}`,
                  borderRadius: { xs: "8px", sm: "12px" },
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.2s",
                  "&:hover": { transform: isMobile ? "none" : "scale(1.02)" },
                }}
              >
                <StatBox
                  title={item.value}
                  subtitle={isMobile ? item.shortLabel : item.label}
                  icon={<NotificationsOutlinedIcon sx={{ color: "#4cbed8", fontSize: { xs: 24, sm: 28, md: 32 } }} />}
                />
              </Box>
            </Grid>
          ))}

          {/* Graphiques */}
          {charts.map((chart, i) => (
            <Grid item xs={12} sm={12} md={6} lg={6} key={i}>
              <Box
                p={{ xs: 1.5, sm: 2 }}
                sx={{
                  minHeight: { xs: "300px", sm: "360px", md: "400px" },
                  background: alpha("#ffffff", isDark ? 0.05 : 0.9),
                  borderRadius: { xs: "8px", sm: "12px" },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  overflow: "hidden",
                }}
              >
                <SectionTitle sx={{ mb: { xs: 1, sm: 1.5 } }}>
                  {isMobile ? chart.shortTitle : chart.title}
                </SectionTitle>

                {chart.data.length === 0 ? (
                  <Box
                    flexGrow={1}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    color={colors.grey[200]}
                    minHeight={{ xs: 200, sm: 250, md: 280 }}
                  >
                    <NotificationsOutlinedIcon sx={{ fontSize: { xs: 40, sm: 50, md: 60 }, opacity: 0.3, mb: { xs: 1, sm: 2 } }} />
                    <Typography variant="body2" align="center" sx={{ px: 2, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      Aucune alerte pour cette société
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    flexGrow={1}
                    minHeight={{ xs: 220, sm: 280, md: 320 }}
                    sx={{
                      position: "relative",
                      width: "100%",
                      overflow: "hidden",
                      "& canvas": { maxHeight: "100% !important", width: "100% !important" },
                      [theme.breakpoints.down("sm")]: { overflowX: "auto", overflowY: "hidden" },
                    }}
                  >

                    <BarChart
                      isDashboard={true}
                      data={[{ id: "alertes", color: "#2a5fa9", data: chart.data }]}
                      onBarClick={(barLabel) => handleChartClick(barLabel, chart.type)}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </StyledCard>


      <AlerteDetailsModal
        key={`${modal.alerteData?.nom_alerte}-${modal.type}`}
        open={modal.open}
        onClose={handleCloseModal}
        alerteData={modal.alerteData}
        type={modal.type}
        token={token}
        role={role}
        onItemUpdated={handleItemUpdated}  
      />
    </Container>
  );
};

export default Dashboard;